/**
 * @file React hook — useToolCitation.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_TOOL_CITATION,
  toolCitationService,
  type ToolCitationConfig,
} from "../services/toolCitationService";

/** Hook: use Tool Citation. */
export function useToolCitation() {
  const [config, setConfig] = useState<Pick<ToolCitationConfig, "menuTitle" | "description">>(
    DEFAULT_TOOL_CITATION
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await toolCitationService.get();
      if (response.success && response.data) {
        setConfig({
          menuTitle: response.data.menuTitle,
          description: response.data.description,
        });
        setError(null);
      } else {
        setError(response.message || "Failed to load citation text");
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to load citation text";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    menuTitle: config.menuTitle,
    description: config.description,
    loading,
    error,
    refetch: load,
  };
}
